<div mat-dialog-content>
  <ng-container *ngIf="!rawBlobUrl; else showImage">
    <mat-spinner class="spinner" color="primary"></mat-spinner>
  </ng-container>
  <ng-template #showImage>
    <div class="modal-content-wrapper">
      
      <!-- Image Section -->
      <div class="image-section">

        <!-- Annotation Toolbar -->
        <div class="annotation-toolbar" *ngIf="hasAnyAnnotationTool || hasAiPredictions">
          <!-- Drawing Tools -->
          <button *ngIf="enableRectTool" mat-icon-button class="toolbar-btn" [class.active]="activeTool === 'rect'" (click)="setActiveTool('rect')" matTooltip="Draw bounding box">
            <mat-icon>crop_square</mat-icon>
          </button>
          <div class="toolbar-separator" *ngIf="enableRectTool && (enablePolygonTool || enableBrushTool)"></div>
          <button *ngIf="enablePolygonTool" mat-icon-button class="toolbar-btn" [class.active]="activeTool === 'polygon'" (click)="setActiveTool('polygon')" matTooltip="Draw polygon">
            <mat-icon>polyline</mat-icon>
          </button>
          <button *ngIf="enableBrushTool" mat-icon-button class="toolbar-btn" [class.active]="activeTool === 'brush'" (click)="setActiveTool('brush')" matTooltip="Draw with brush">
            <mat-icon>brush</mat-icon>
          </button>
          <div class="brush-size-control" *ngIf="activeTool === 'brush'">
            <mat-icon class="brush-size-icon" matTooltip="Brush size">circle</mat-icon>
            <mat-slider
              min="2"
              max="80"
              step="1"
              thumbLabel
              [value]="brushSize"
              (valueChange)="brushSize = $event ?? brushSize"
              class="brush-size-slider">
            </mat-slider>
            <span class="brush-size-label">{{ brushSize }}px</span>
          </div>
          <button *ngIf="enableEditPointsTool" mat-icon-button class="toolbar-btn" [class.active]="activeTool === 'edit-points'" (click)="setActiveTool('edit-points')" matTooltip="Reposition points">
            <mat-icon>adjust</mat-icon>
          </button>
          <button *ngIf="enableMoveShapeTool" mat-icon-button class="toolbar-btn" [class.active]="activeTool === 'move-shape'" (click)="setActiveTool('move-shape')" matTooltip="Move shape">
            <mat-icon>open_with</mat-icon>
          </button>

          <div class="toolbar-separator"></div>

          <!-- Actions -->
          <button mat-icon-button class="toolbar-btn" (click)="undoAnnotation()" matTooltip="Undo">
            <mat-icon>undo</mat-icon>
          </button>
          <button mat-icon-button class="toolbar-btn" (click)="redoAnnotation()" [disabled]="redoStack.length === 0" matTooltip="Redo">
            <mat-icon>redo</mat-icon>
          </button>
          <button mat-icon-button class="toolbar-btn" (click)="clearAnnotations()" matTooltip="Clear annotations">
            <mat-icon>delete_sweep</mat-icon>
          </button>

          <ng-container *ngIf="hasAiPredictions">
            <div class="toolbar-separator"></div>
            <div class="prediction-toggle-wrapper">
              <mat-slide-toggle
                class="prediction-toggle"
                [checked]="showAiPredictions"
                (change)="toggleAiPredictionsVisibility()"
                [attr.aria-label]="showAiPredictions ? 'Detected anomaly areas on' : 'Detected anomaly areas off'"
                [matTooltip]="showAiPredictions ? 'Hide detected anomaly areas' : 'Show detected anomaly areas'"
              >
              </mat-slide-toggle>
              <span class="prediction-toggle-label">Anomalys</span>
            </div>
          </ng-container>

          <!-- Auto save indicator — centered in toolbar -->
          <div class="toolbar-autosave">
            <span class="autosave-dot"></span>
            <span>Auto save activated</span>
          </div>

          <!-- Keyboard shortcut hints pushed to the right end of the toolbar -->
          <div style="margin-left: auto; display: flex; gap: 10px; align-items: center; font-size: 0.72em; color: rgba(255,255,255,0.75);">
            <ng-container *ngIf="hasPrevious || hasNext">
              <button mat-icon-button class="toolbar-btn" (click)="navigatePrevious()" [disabled]="!hasPrevious" matTooltip="Previous image (←)">
                <mat-icon>chevron_left</mat-icon>
              </button>
              <button mat-icon-button class="toolbar-btn" (click)="navigateNext()" [disabled]="!hasNext" matTooltip="Next image (→)">
                <mat-icon>chevron_right</mat-icon>
              </button>
              <span style="width:1px;height:22px;background:rgba(255,255,255,0.2);display:inline-block;margin:0 2px;"></span>
            </ng-container>
            <div class="toolbar-shortcut-stack">
              <span>
                <kbd style="background:rgba(255,255,255,0.15);border-color:rgba(255,255,255,0.35);color:#fff;">F11</kbd>
                Fullscreen
              </span>
              <span>
                <kbd style="background:rgba(255,255,255,0.15);border-color:rgba(255,255,255,0.35);color:#fff;">Scroll</kbd>
                Zoom
              </span>
            </div>
            <span style="width:1px;height:22px;background:rgba(255,255,255,0.2);display:inline-block;margin:0 2px;"></span>
            <div class="toolbar-shortcut-stack">
              <span style="display:flex;align-items:center;gap:4px;">
                <kbd style="background:rgba(255,255,255,0.15);border-color:rgba(255,255,255,0.35);color:#fff;">Esc</kbd>
                Leave
              </span>
              <span *ngIf="enablePolygonTool">
                <kbd style="background:rgba(255,255,255,0.15);border-color:rgba(255,255,255,0.35);color:#fff;">Enter</kbd>
                Close polygon
              </span>
            </div>
          </div>
        </div>

        <div
          class="image-container"
          [class.zoomed]="isZoomed"
          #imageContainer
          (mousedown)="onImageContainerMouseDown($event, imageContainer)"
          (mousemove)="onImageContainerMouseMove($event, imageContainer)"
          (mouseup)="onImageContainerMouseUp($event)"
          (mouseleave)="isDragging = false; onAiPredictionAreaMouseLeave()"
          (wheel)="onScroll($event, imageContainer)"
        >
          <img
            #theImage
            [src]="safeUrl"
            alt="S3 Image"
            class="zoomable-image"
            [class.zoomed]="isZoomed"
            [style.transform]="'translate(' + panX + 'px, ' + panY + 'px) scale(' + zoomLevel + ')'"
            [style.transform-origin]="'top left'"
            draggable="false"
            (load)="onImageLoad()"
          >
          <canvas
            #annotationCanvas
            class="annotation-canvas"
            [style.transform]="'scale(' + zoomLevel + ')'"
            [style.transform-origin]="'top left'"
            [style.pointer-events]="activeTool ? 'auto' : 'none'"
            [style.cursor]="canvasCursor"
            (mousedown)="onCanvasMouseDown($event)"
            (mousemove)="onCanvasMouseMove($event)"
            (mouseup)="onCanvasMouseUp($event)"
            (dblclick)="onCanvasDblClick($event)"
            (mouseleave)="onCanvasMouseLeave()"
          ></canvas>

          <!-- Annotation Label Popup -->
          <div
            *ngIf="pendingAnnotation"
            class="annotation-label-popup"
            [style.left.px]="pendingAnnotationScreenPos?.x"
            [style.top.px]="pendingAnnotationScreenPos?.y"
            (mousedown)="$event.stopPropagation()"
          >
            <mat-form-field class="popup-field" appearance="outline">
              <input
                *ngIf="allowUserToEditClasses; else predefinedClassSelect"
                matInput
                #labelInputRef
                placeholder="Enter class..."
                [(ngModel)]="labelInput"
                [matAutocomplete]="labelAc"
                (keydown.enter)="confirmLabel()"
                (keydown.escape)="cancelLabel()"
              >
              <ng-template #predefinedClassSelect>
                <mat-select
                  #classSelectRef
                  placeholder="Select class..."
                  [(value)]="labelInput"
                  (selectionChange)="confirmLabel()"
                >
                  <mat-option *ngFor="let cls of pendingAnnotationClasses" [value]="cls">
                    <span class="ac-color-dot" [style.background-color]="classColors[cls] || '#00e676'"></span>
                    {{ cls }}
                  </mat-option>
                </mat-select>
              </ng-template>
              <mat-autocomplete #labelAc (optionSelected)="labelInput = $event.option.value; confirmLabel()">
                <mat-option *ngFor="let cls of filteredAnnotationClasses" [value]="cls">
                  <span class="ac-color-dot" [style.background-color]="classColors[cls] || '#00e676'"></span>
                  {{ cls }}
                </mat-option>
              </mat-autocomplete>
            </mat-form-field>
            <button mat-icon-button class="popup-btn confirm" (click)="confirmLabel()" matTooltip="Confirm">
              <mat-icon>check</mat-icon>
            </button>
            <button mat-icon-button class="popup-btn cancel" (click)="cancelLabel()" matTooltip="Cancel">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <!-- AI anomaly hover tooltip (read-only) -->
          <div
            *ngIf="hoveredAiPrediction as hovered"
            class="ai-prediction-tooltip"
            [style.left.px]="hovered.screenPos.x"
            [style.top.px]="hovered.screenPos.y"
          >
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.label"><b>Label:</b> {{ hovered.box.label }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.predictionType"><b>Prediction:</b> {{ hovered.box.predictionType }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.probability !== undefined"><b>Probability:</b> {{ hovered.box.probability }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.confidence !== undefined"><b>Confidence:</b> {{ hovered.box.confidence }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.modelId"><b>Model ID:</b> {{ hovered.box.modelId }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.threshold !== undefined"><b>Threshold:</b> {{ hovered.box.threshold }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.blobSize !== undefined"><b>Blob size:</b> {{ hovered.box.blobSize }}</div>
            <div class="ai-prediction-tooltip-row" *ngIf="hovered.box.suggestion"><b>Suggestion:</b> {{ hovered.box.suggestion }}</div>
          </div>
        </div>
        
        <!-- Navigation Arrows -->
        <button 
          mat-icon-button 
          class="nav-arrow nav-arrow-left"
          *ngIf="hasPrevious"
          (click)="navigatePrevious(); $event.stopPropagation()"
          (mousedown)="$event.stopPropagation()"
          matTooltip="Previous"
          matTooltipPosition="right"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
        
        <button 
          mat-icon-button 
          class="nav-arrow nav-arrow-right"
          *ngIf="hasNext"
          (click)="navigateNext(); $event.stopPropagation()"
          (mousedown)="$event.stopPropagation()"
          matTooltip="Next"
          matTooltipPosition="left"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>

      <!-- Form Section -->
      <div class="form-section" [ngClass]="{ readyToSendRow: isLabelingComplete }">
        <form [formGroup]="form">
          <formly-form
            (modelChange)="onSelected()"
            [model]="model"
            [fields]="fields"
            [form]="form"
          ></formly-form>
        </form>

        <!-- Image class selector as radio buttons -->
        <div *ngIf="hasImageStrategy" style="margin-top: 8px; margin-bottom: 4px;">
          <mat-radio-group
            [value]="$any(model)['Class']"
            (change)="onImageClassSelected($event.value)"
            style="display: grid; gap: 4px;"
            [style.grid-template-columns]="imageClassesUseTwoColumns ? '1fr 1fr' : '1fr'"
          >
            <mat-radio-button *ngFor="let cls of imageClasses" [value]="cls">
              {{ cls }}<mat-icon
                *ngIf="classDescriptions[cls]"
                style="font-size:14px;height:14px;width:14px;opacity:0.5;vertical-align:middle;margin-left:4px;cursor:default;"
                [matTooltip]="classDescriptions[cls]"
                matTooltipPosition="right"
              >info_outline</mat-icon>
            </mat-radio-button>
          </mat-radio-group>
        </div>

        <!-- Class Manager -->
        <div class="class-manager" *ngIf="hasClassManager">
          <div class="class-manager-header">Classes</div>

          <div *ngIf="hasRectClassManager">
            <div class="annotation-list-header">BoundingBox Classes</div>
            <div class="class-list" *ngIf="rectClasses.length > 0">
              <div *ngFor="let cls of rectClasses" class="class-chip" [style.border-color]="classColors[cls] || '#777'">
                <span
                  class="chip-info-area"
                  [matTooltip]="getClassInfoTooltip(cls)"
                  matTooltipPosition="above"
                >
                  <span class="class-dot" [style.background-color]="classColors[cls] || '#777'"></span>
                  <mat-icon class="class-chip-info-icon">info_outline</mat-icon>
                  <span class="class-name">{{ cls }}</span>
                </span>
                <button *ngIf="allowUserToEditClasses" mat-icon-button class="class-chip-delete" (click)="removeRectClass(cls)" matTooltip="Delete class">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <div class="class-add-row" *ngIf="allowUserToEditClasses">
              <mat-form-field appearance="outline" class="class-input-field">
                <input matInput placeholder="New Bbox Class..." [(ngModel)]="newRectClassInput" (keydown.enter)="addRectClass()">
              </mat-form-field>
              <button mat-icon-button class="class-add-btn" (click)="addRectClass()" [disabled]="!newRectClassInput.trim()" matTooltip="Add class">
                <mat-icon>add_circle</mat-icon>
              </button>
            </div>
          </div>

          <div *ngIf="hasPolygonBrushClassManager">
            <div class="annotation-list-header">Polygon/Brush Classes</div>
            <div class="class-list" *ngIf="polygonBrushClasses.length > 0">
              <div *ngFor="let cls of polygonBrushClasses" class="class-chip" [style.border-color]="classColors[cls] || '#777'">
                <span
                  class="chip-info-area"
                  [matTooltip]="getClassInfoTooltip(cls)"
                  matTooltipPosition="above"
                >
                  <span class="class-dot" [style.background-color]="classColors[cls] || '#777'"></span>
                  <mat-icon class="class-chip-info-icon">info_outline</mat-icon>
                  <span class="class-name">{{ cls }}</span>
                </span>
                <button *ngIf="allowUserToEditClasses" mat-icon-button class="class-chip-delete" (click)="removePolygonBrushClass(cls)" matTooltip="Delete class">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <div class="class-add-row" *ngIf="allowUserToEditClasses">
              <mat-form-field appearance="outline" class="class-input-field">
                <input matInput placeholder="New Polygon/Brush Class..." [(ngModel)]="newPolygonBrushClassInput" (keydown.enter)="addPolygonBrushClass()">
              </mat-form-field>
              <button mat-icon-button class="class-add-btn" (click)="addPolygonBrushClass()" [disabled]="!newPolygonBrushClassInput.trim()" matTooltip="Add class">
                <mat-icon>add_circle</mat-icon>
              </button>
            </div>
          </div>

          <!-- Annotation List -->
          <div class="annotation-list" *ngIf="annotations.length > 0">
            <div class="annotation-list-header-row">
              <div class="annotation-list-header">Annotations</div>
              <div class="annotation-list-global-actions">
                <button
                  mat-icon-button
                  class="annotation-visibility-btn"
                  (click)="toggleAllAnnotationsVisibility()"
                  [class.annotation-toggle-off]="areAllAnnotationsHidden()"
                  [matTooltip]="areAllAnnotationsHidden() ? 'Show all annotations' : 'Hide all annotations'"
                >
                  <mat-icon>{{ areAllAnnotationsHidden() ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <button
                  mat-icon-button
                  class="annotation-visibility-btn"
                  (click)="toggleAllAnnotationClassNameVisibility()"
                  [class.annotation-toggle-off]="areAllAnnotationClassNamesHidden()"
                  [matTooltip]="areAllAnnotationClassNamesHidden() ? 'Show all class labels' : 'Hide all class labels'"
                >
                  <mat-icon>sell</mat-icon>
                </button>
              </div>
            </div>
            <div *ngFor="let ann of annotations; let i = index" class="annotation-list-item">
              <span class="annotation-index">{{ i + 1 }}.</span>
              <span class="annotation-color-dot" [style.background-color]="ann.color || '#777'"></span>
              <ng-container *ngIf="editingAnnotationIdx === i; else showLabel">
                <mat-select
                  #annotationClassSelect
                  class="annotation-class-select"
                  [value]="ann.label || ''"
                  (selectionChange)="changeAnnotationClass(i, $event.value)"
                  placeholder="Choose class..."
                >
                  <mat-option *ngFor="let cls of getClassesForAnnotation(ann)" [value]="cls">
                    <span class="ac-color-dot" [style.background-color]="classColors[cls] || '#777'"></span>
                    {{ cls }}
                  </mat-option>
                </mat-select>
                <button mat-icon-button class="annotation-edit-btn" (click)="toggleEditAnnotation(i)" matTooltip="Cancel">
                  <mat-icon>close</mat-icon>
                </button>
              </ng-container>
              <ng-template #showLabel>
                <span class="annotation-type-label">
                  <ng-container *ngIf="ann.label"> 
                    <!-- !needs to be changed back to "<span class="annotation-type-label">
                  !{{ ann.type }}<ng-container *ngIf="ann.label"> – {{ ann.label }}</ng-container>" -->
                    <span *ngIf="ann.type != 'rect'">{{ ann.type }}</span>
                    <span *ngIf="ann.type == 'rect'">bbox</span>
                    – {{ ann.label }}</ng-container>
                </span>
                <button
                  mat-icon-button
                  class="annotation-visibility-btn"
                  (click)="toggleAnnotationVisibility(i)"
                >
                  <mat-icon>{{ isAnnotationVisible(ann) ? 'visibility' : 'visibility_off' }}</mat-icon>
                </button>
                <button
                  mat-icon-button
                  class="annotation-visibility-btn"
                  (click)="toggleAnnotationClassNameVisibility(i)"
                  [class.annotation-toggle-off]="!isAnnotationClassNameVisible(ann)"
                  [disabled]="!isAnnotationVisible(ann)"
                  [attr.tabindex]="!isAnnotationVisible(ann) ? -1 : null"
                >
                  <mat-icon>sell</mat-icon>
                </button>
                <button mat-icon-button class="annotation-edit-btn" (click)="toggleEditAnnotation(i)" matTooltip="Change class">
                  <mat-icon>edit</mat-icon>
                </button>
              </ng-template>
              <button mat-icon-button class="annotation-delete-btn" (click)="removeAnnotation(i)" matTooltip="Delete annotation">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-template>
</div>

<div mat-dialog-actions align="end">
  <button mat-raised-button class="clear-labels-button" (click)="onNoClick()">Cancel</button>
  <button mat-raised-button class="clear-labels-button" (click)="clearDropdowns()">
    <mat-icon>clear</mat-icon>
    Clear Labels
  </button>
  <button mat-raised-button color="primary" (click)="downloadImage()">
    <mat-icon>download</mat-icon>
    Download
  </button>
  <button
    mat-raised-button
    color="accent"
    (click)="save()"
    cdkFocusInitial
  >
    Save
  </button>
</div>